import React, { useState } from 'react';
import { Card, Steps, message } from 'antd';
import KioskFeedbackForm from '../../components/Demo/Feedback/KioskFeedbackForm';
import '../../components/Demo/Feedback/KioskFeedbackForm.css';

interface KioskFeedbackData {
  // Step 0: User Information
  name?: string;
  email?: string;
  company?: string;
  
  // Step 1: Topic Selection & Session Rating
  topics?: string[];
  otherTopicSpecify?: string;
  sessionRating?: number;
  sessionRatingComments?: string;
  
  // Step 2: Session Insights (using existing backend fields)
  mostValuable?: string; // Maps to backend's mostValuable field
  
  // Step 3: Discussion Interest
  interestedInDiscussions?: string;
  discussionTopics?: string;
  
  // Step 4: Project Planning & Additional Comments
  projectStatus?: string;
  additionalComments?: string;
}

const ComprehensiveFeedbackPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [feedbackData, setFeedbackData] = useState<KioskFeedbackData>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const steps = [
    {
      title: 'User Info',
      description: 'Your details',
    },
    {
      title: 'Sessions & Rating',
      description: 'Session feedback',
    },
    {
      title: 'Session Insights',
      description: 'Session value',
    },
    {
      title: 'Discussion Interest',
      description: 'Expert discussions',
    },
    {
      title: 'Project & Comments',
      description: 'Project status',
    },
    {
      title: 'Done',
      description: 'Complete',
    },
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepSubmit = (stepData: Partial<KioskFeedbackData>) => {
    setFeedbackData(prev => ({ ...prev, ...stepData }));
    
    // If this is the last form step (Project & Comments step, which is now index 4), submit to backend
    if (currentStep === steps.length - 2) {
      handleFinalSubmit({ ...feedbackData, ...stepData });
    } else {
      handleNext();
    }
  };

  const handleFinalSubmit = async (completeData: KioskFeedbackData) => {
    setIsSubmitting(true);
    try {
      const requestData = {
        // Map kiosk data to backend format
        topic: completeData.topics?.[0] || 'Other', // Use first topic or 'Other'
        otherTopicSpecify: completeData.otherTopicSpecify,
        rating: completeData.sessionRating || 5, // Required field, default to 5
        sessionRating: completeData.sessionRating === 5 ? 'Excellent' : 
                      completeData.sessionRating === 4 ? 'Good' : 
                      completeData.sessionRating === 3 ? 'Average' : 
                      completeData.sessionRating === 2 ? 'Average' : 
                      completeData.sessionRating === 1 ? 'Average' : 'Good',
        mostValuable: completeData.mostValuable,
        contactForDiscussion: completeData.interestedInDiscussions === 'Yes',
        
        // NEW: Store fields separately instead of concatenating
        sessionRatingComments: completeData.sessionRatingComments,
        discussionTopics: completeData.discussionTopics,
        projectStatus: completeData.projectStatus,
        allTopics: completeData.topics ? JSON.stringify(completeData.topics) : undefined,
        
        // Keep only actual additional comments
        additionalComments: completeData.additionalComments,
        
        // User information
        name: completeData.name || 'Anonymous User',
        email: completeData.email || 'anonymous@demo.local',
        company: completeData.company,
        sessionId: `kiosk_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      };
      
      console.log('feedback api', requestData);
      
      const response = await fetch('/api/v1/demo/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        message.success('Thank you for your feedback!');
        handleNext();
      } else {
        let errorMessage = 'Failed to submit feedback';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
          console.error('Feedback submission error:', errorData);
        } catch (parseError) {
          // If response is not JSON (e.g., HTML error page), use status text
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
          console.error('Response parsing error:', parseError);
          console.error('Response status:', response.status, response.statusText);
        }
        message.error(errorMessage);
      }
    } catch (error) {
      message.error('Network error. Please try again.');
      console.error('Error submitting feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setCurrentStep(0);
    setFeedbackData({});
    setIsSubmitting(false);
  };

  const renderStepContent = () => {
    return (
      <KioskFeedbackForm
        currentStep={currentStep}
        initialData={feedbackData}
        onSubmit={handleStepSubmit}
        onBack={handlePrev}
        showBackButton={currentStep > 0}
        isSubmitting={isSubmitting}
        onReset={handleReset}
      />
    );
  };

  return (
    <div className="h-screen bg-gradient-to-br from-blue-50 flex flex-col overflow-hidden" style={{backgroundImage: 'linear-gradient(to bottom right, rgb(239 246 255), #a8ccde)'}}>
      <div className="flex-1 flex flex-col max-w-7xl mx-auto w-full px-4 py-3">
        {/* Progress Steps - Compact for kiosk */}
        <Card className="mb-3 shadow-md">
          <Steps 
            current={currentStep} 
            items={steps}
            size="small"
            className="px-4 py-2"
          />
        </Card>

        {/* Main Content - Optimized for touch interaction */}
        <Card className="shadow-lg flex-1 flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto">
            {renderStepContent()}
          </div>
        </Card>

        {/* Footer with step indicator */}
        <div className="text-center mt-3 text-gray-600">
          <p className="text-sm font-medium">
            Step {currentStep + 1} of {steps.length} • Estimated time: {Math.max(2 - currentStep, 0)} minutes remaining
          </p>
        </div>
      </div>
    </div>
  );
};

export default ComprehensiveFeedbackPage;